import { Service } from '@/services/api';
import {
  AIModel,
  AIPrompt,
  CreatePromptRequest,
  UpdatePromptRequest,
  UpdatePromptResponse,
  DeletePromptResponse,
  InvokeModelRequest,
  InvokeModelResponse,
  AIPromptResult,
  CreatePromptResultRequest,
  CreatePromptResultResponse,
  AIPromptMapping,
  AIPromptMappingRequest,
  AIPromptMappingUpdateRequest,
  BulkUpdatePromptMappingsRequest,
  BulkUpdatePromptMappingsResponse,
  PublishPromptVersionResponse,
  InvokeModelAsyncResponse,
  ModelInvocationStatusResponse,
  ModelInvocation,
  FieldOverrideListResponse,
  ReviewEditInput,
  ReviewLineOpInput,
  MergedPromptResultResponse,
  AIPromptResultReview,
  AIPromptResultWithReviews,
  PromptResultsWithReviewsResponse,
  PromptResultsFilter,
  PromptResultFilterOptions,
  CreateReviewResponse,
  ReviewPreviewResponse,
  BinderValidationStatus,
  UnvalidatedBindersByCoverageType,
  ModelPerformance,
  PromptAccuracy,
  BulkInvokeRequest,
  BulkInvokeResponse,
  BulkInvokeCounts,
  ExtractionFailuresFilter,
  ExtractionFailuresResponse,
  PromptDesignation,
} from '@/types/ai';
import {
  PromptField,
  CreatePromptFieldRequest,
  UpdatePromptFieldRequest,
} from '@/types/PromptField';
import { Document } from '@/types/ui/PromptManager';

class AIService extends Service {
  async getPrompt(id: number, version: string): Promise<AIPrompt> {
    return await this.get<AIPrompt>(`/v2/ai/prompts/${id}?version=${version}`).then(this.handleResponse);
  }

  async getAllModels(): Promise<AIModel[]> {
    return await this.get<AIModel[]>('/v2/ai/models').then(this.handleResponse);
  }

  async getAllPrompts(designation?: PromptDesignation, opts?: { includeSchema?: boolean }): Promise<AIPrompt[]> {
    const params = new URLSearchParams();
    if (designation) {
      params.set('designation', designation);
    }
    if (opts?.includeSchema === false) {
      params.set('includeSchema', 'false');
    }
    const query = params.size > 0 ? `?${params}` : '';
    return await this.get<AIPrompt[]>(`/v2/ai/prompts/versions/all${query}`).then(this.handleResponse);
  }

  async createPrompt(request: CreatePromptRequest): Promise<{ promptId: number }> {
    return await this.post<{promptId: number}>('/v2/ai/prompts', request).then(this.handleResponse);
  }

  async updatePrompt(id: number, request: UpdatePromptRequest): Promise<UpdatePromptResponse> {
    return await this.put<UpdatePromptResponse>(`/v2/ai/prompts/${id}`, request).then(this.handleResponse);
  }

  async updatePromptDraftOnly(id: number, request: UpdatePromptRequest): Promise<UpdatePromptResponse> {
    return await this.patch<UpdatePromptResponse>(`/v2/ai/prompts/${id}`, request).then(this.handleResponse);
  }

  async publishPromptVersion(id: number): Promise<PublishPromptVersionResponse> {
    return await this.post<PublishPromptVersionResponse>(`/v2/ai/prompts/${id}/publish`, {}).then(this.handleResponse);
  }

  async deletePrompt(id: number, version: number): Promise<DeletePromptResponse> {
    return await this.delete<DeletePromptResponse>(`/v2/ai/prompts/${id}/versions/${version}`).then(this.handleResponse);
  }

  async deleteAllVersionsOfPrompt(id: number): Promise<DeletePromptResponse> {
    return await this.delete<DeletePromptResponse>(`/v2/ai/prompts/${id}`).then(this.handleResponse);
  }

  async invokeModel(request: InvokeModelRequest): Promise<InvokeModelResponse> {
    return await this.post<InvokeModelResponse>(`/v2/ai/invoke-model`, request).then(this.handleResponse);
  }

  async invokeModelAsync(request: InvokeModelRequest): Promise<InvokeModelAsyncResponse> {
    return await this.post<InvokeModelAsyncResponse>(`/v2/ai/invoke-model-async`, request).then(this.handleResponse);
  }

  async getInvocationStatus(invocationId: string): Promise<ModelInvocationStatusResponse> {
    return await this.get<ModelInvocationStatusResponse>(`/v2/ai/invocation/${invocationId}/status`).then(this.handleResponse);
  }

  async getInvocationResult(invocationId: string): Promise<AIPromptResult> {
    return await this.get<AIPromptResult>(`/v2/ai/invocation/${invocationId}/result`).then(this.handleResponse);
  }

  async getActiveInvocations(limit?: number): Promise<ModelInvocation[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = `/v2/ai/invocations/active${queryString ? `?${queryString}` : ''}`;

    return await this.get<ModelInvocation[]>(url).then(this.handleResponse);
  }

  async getExtractionFailures(
    filter: ExtractionFailuresFilter = {},
  ): Promise<ExtractionFailuresResponse> {
    const params = new URLSearchParams();
    if (filter.limit !== undefined) params.append('limit', filter.limit.toString());
    if (filter.offset !== undefined) params.append('offset', filter.offset.toString());
    if (filter.days !== undefined) params.append('days', filter.days.toString());
    if (filter.accountSearch) params.append('accountSearch', filter.accountSearch);
    if (filter.promptName) params.append('promptName', filter.promptName);
    if (filter.userEmail) params.append('userEmail', filter.userEmail);

    const queryString = params.toString();
    const url = `/v2/ai/extraction-failures${queryString ? `?${queryString}` : ''}`;

    return await this.get<ExtractionFailuresResponse>(url).then(this.handleResponse);
  }

  async getAllDocuments(): Promise<Document[]> {
    return await this.get<Document[]>('/v2/ai/documents').then(this.handleResponse);
  }

  async uploadDocument(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    return await this.post<string>(`/v2/ai/documents`, formData).then(this.handleInsertResponse);
  }

  async deleteDocument(s3Key: string): Promise<void> {
    await this.delete(`/v2/ai/documents/${encodeURIComponent(s3Key)}`).then(this.handleResponse);
  }

  async downloadDocument(s3Key: string, convertToPdf: boolean = false): Promise<string> {
    return await this.get(`/v2/ai/documents/${encodeURIComponent(s3Key)}/download`, { convertToPdf }, {
      responseType: 'blob',
    }).then(this.handleFile);
  }

  async createPromptResult(request: CreatePromptResultRequest): Promise<CreatePromptResultResponse> {
    return await this.post<CreatePromptResultResponse>('/v2/ai/prompt-results', request).then(this.handleResponse);
  }

  async getPromptResults(limit?: number, offset?: number): Promise<AIPromptResult[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const queryString = params.toString();
    const url = `/v2/ai/prompt-results${queryString ? `?${queryString}` : ''}`;

    return await this.get<AIPromptResult[]>(url).then(this.handleResponse);
  }

  async getPromptMappings(): Promise<AIPromptMapping[]> {
    return await this.get<AIPromptMapping[]>('/v2/ai/prompt-mappings').then(this.handleResponse);
  }

  async createPromptMapping(mapping: AIPromptMappingRequest): Promise<AIPromptMapping> {
    return await this.post<AIPromptMapping>('/v2/ai/prompt-mappings', { ...mapping }).then(this.handleResponse);
  }

  async updatePromptMapping(id: number, request: AIPromptMappingUpdateRequest): Promise<void> {
    await this.put(`/v2/ai/prompt-mappings/${id}`, request).then(this.handleResponse);
  }

  async deletePromptMapping(id: string): Promise<void> {
    await this.delete(`/v2/ai/prompt-mappings/${id}`).then(this.handleResponse);
  }

  async bulkUpdatePromptMappings(
    request: BulkUpdatePromptMappingsRequest,
  ): Promise<BulkUpdatePromptMappingsResponse> {
    return await this.post<BulkUpdatePromptMappingsResponse>(
      '/v2/ai/prompt-mappings/bulk-update',
      request,
    ).then(this.handleResponse);
  }

  // Prompt Field Library methods
  async getAllPromptFields(category?: string): Promise<PromptField[]> {
    const url = category ? `/v2/ai/prompt-fields?category=${encodeURIComponent(category)}` : '/v2/ai/prompt-fields';
    return await this.get<PromptField[]>(url).then(this.handleResponse);
  }

  async getPromptField(id: number): Promise<PromptField> {
    return await this.get<PromptField>(`/v2/ai/prompt-fields/${id}`).then(this.handleResponse);
  }

  async createPromptField(request: CreatePromptFieldRequest): Promise<PromptField> {
    return await this.post<PromptField>('/v2/ai/prompt-fields', request).then(this.handleResponse);
  }

  async updatePromptField(id: number, request: UpdatePromptFieldRequest): Promise<PromptField> {
    return await this.put<PromptField>(`/v2/ai/prompt-fields/${id}`, request).then(this.handleResponse);
  }

  async deletePromptField(id: number): Promise<void> {
    await this.delete(`/v2/ai/prompt-fields/${id}`).then(this.handleResponse);
  }

  // Field Override methods (CAC-5113: AI Field Override Review Mode)
  async getFieldOverrides(promptResultId: number): Promise<FieldOverrideListResponse> {
    return await this.get<FieldOverrideListResponse>(
      `/v2/ai/prompt-results/${promptResultId}/field-overrides`
    ).then(this.handleResponse);
  }

  async getMergedPromptResult(promptResultId: number): Promise<MergedPromptResultResponse> {
    return await this.get<MergedPromptResultResponse>(
      `/v2/ai/prompt-results/${promptResultId}/merged`
    ).then(this.handleResponse);
  }

  // Preview the changes a review would apply. Reviewer edits and structural line
  // operations are not persisted until the review completes, so the in-flight set is sent
  // in the request body.
  async getReviewPreview(
    promptResultId: number,
    edits: ReviewEditInput[],
    lineOps?: ReviewLineOpInput[]
  ): Promise<ReviewPreviewResponse> {
    return await this.post<ReviewPreviewResponse>(
      `/v2/ai/prompt-results/${promptResultId}/review/preview`,
      { edits, lineOps }
    ).then(this.handleResponse);
  }

  // Review methods (CAC-5113: Track who reviewed extraction results)
  // Completing a review is the only point at which reviewer edits are persisted; the edit
  // set is written as immutable review-edit rows, and line operations as line-review rows.
  async markAsReviewed(
    promptResultId: number,
    edits: ReviewEditInput[],
    lineOps?: ReviewLineOpInput[]
  ): Promise<CreateReviewResponse> {
    return await this.post<CreateReviewResponse>(
      `/v2/ai/prompt-results/${promptResultId}/review`,
      { edits, lineOps }
    ).then(this.handleResponse);
  }

  async getReviews(promptResultId: number): Promise<AIPromptResultReview[]> {
    return await this.get<AIPromptResultReview[]>(
      `/v2/ai/prompt-results/${promptResultId}/reviews`
    ).then(this.handleResponse);
  }

  async getBinderValidationStatus(coverageTypeIds?: number[]): Promise<BinderValidationStatus> {
    const params = new URLSearchParams();
    coverageTypeIds?.forEach((id) => params.append('coverageTypeId', id.toString()));
    const queryString = params.toString();
    const url = `/v2/ai/monitoring/binder-validation-status${queryString ? `?${queryString}` : ''}`;
    return await this.get<BinderValidationStatus>(url).then(this.handleResponse);
  }

  async getUnvalidatedBindersByCoverageType(): Promise<UnvalidatedBindersByCoverageType[]> {
    return await this.get<UnvalidatedBindersByCoverageType[]>(
      '/v2/ai/monitoring/unvalidated-binders-by-coverage-type',
    ).then(this.handleResponse);
  }

  async getModelPerformance(days?: number): Promise<ModelPerformance[]> {
    const url = `/v2/ai/monitoring/model-performance${days ? `?days=${days}` : ''}`;
    return await this.get<ModelPerformance[]>(url).then(this.handleResponse);
  }

  async getPromptAccuracy(days?: number): Promise<PromptAccuracy[]> {
    const url = `/v2/ai/monitoring/prompt-accuracy${days ? `?days=${days}` : ''}`;
    return await this.get<PromptAccuracy[]>(url).then(this.handleResponse);
  }

  async getPromptResultWithReviews(id: number): Promise<AIPromptResultWithReviews> {
    return await this.get<AIPromptResultWithReviews>(`/v2/ai/prompt-results/${id}`).then(this.handleResponse);
  }

  async getPromptResultsWithReviews(filter: PromptResultsFilter = {}): Promise<PromptResultsWithReviewsResponse> {
    const body: Record<string, unknown> = {};
    if (filter.pageSize !== undefined) body.pageSize = filter.pageSize;
    if (filter.pageOffset !== undefined) body.pageOffset = filter.pageOffset;
    if (filter.userHasServicingRole) body.userHasServicingRole = true;
    if (filter.userFavorited) body.userFavorited = true;
    if (filter.accountSearch) body.accountSearch = filter.accountSearch;
    if (filter.promptName) body.promptName = filter.promptName;
    if (filter.userEmail) body.userEmail = filter.userEmail;
    if (filter.showValidated) body.showValidated = true;
    return await this.post<PromptResultsWithReviewsResponse>('/v2/ai/prompt-results-with-reviews', body).then(this.handleResponse);
  }

  async getPromptResultFilterOptions(): Promise<PromptResultFilterOptions> {
    return await this.get<PromptResultFilterOptions>('/v2/ai/prompt-results/filter-options').then(this.handleResponse);
  }

  async bulkInvoke(request: BulkInvokeRequest): Promise<BulkInvokeResponse> {
    return await this.post<BulkInvokeResponse>('/v2/ai/bulk-invoke', request).then(this.handleResponse);
  }

  async getBulkInvokeCounts(
    coverageTypeId: number,
    documentTypeId: number,
    options?: { effectiveDateFrom?: string; effectiveDateTo?: string },
  ): Promise<BulkInvokeCounts> {
    const params = new URLSearchParams({
      coverageTypeId: String(coverageTypeId),
      documentTypeId: String(documentTypeId),
    });
    if (options?.effectiveDateFrom) params.set('effectiveDateFrom', options.effectiveDateFrom);
    if (options?.effectiveDateTo) params.set('effectiveDateTo', options.effectiveDateTo);
    return await this.get<BulkInvokeCounts>(`/v2/ai/bulk-invoke/counts?${params.toString()}`).then(this.handleResponse);
  }
}

export const aiService = new AIService();
